import {createContext, use, useRef, type ReactNode} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'
import {createIssueCommentSlice, type IssueCommentSlice, type IssueCommentsSliceOpts} from './slices/issue-comments'
import {createMarkersSlice, type MarkersInfo, type MarkersSlice, type MarkersSliceOpts} from './slices/markers'
import {createPendingReviewSlice, type PendingReviewSlice, type PendingReviewSliceOpts} from './slices/pending-review'
import type {PullRequestState} from '../types'

/** Minimal PR context needed by the layout store (no diff-specific data) */
export type LayoutPullRequestInfo = {
  basePath: string
  id: string
  number: number
  state: PullRequestState
}

export type LayoutStoreState = IssueCommentSlice &
  MarkersSlice &
  PendingReviewSlice & {
    layoutPullRequest: LayoutPullRequestInfo
    layoutStoreActions: {
      hydrateMarkers: (markers: MarkersInfo) => void
    }
  }

type LayoutStoreInitialState = IssueCommentsSliceOpts &
  MarkersSliceOpts &
  PendingReviewSliceOpts & {
    layoutPullRequest: LayoutPullRequestInfo
  }

type LayoutStoreInstance = ReturnType<typeof createLayoutStore>

const LayoutStoreContext = createContext<LayoutStoreInstance | null>(null)

export function useLayoutStore<T>(selector: (state: LayoutStoreState) => T): T {
  const store = use(LayoutStoreContext)
  if (!store) throw new Error('Missing LayoutStoreProvider in the tree')
  return useStore(store, selector)
}

export interface LayoutStoreProviderProps {
  children: ReactNode
  basePath: string
  pullRequestId: string
  pullRequestNumber: number
  pullRequestState: PullRequestState
}

export function LayoutStoreProvider({
  children,
  basePath,
  pullRequestId,
  pullRequestNumber,
  pullRequestState,
}: LayoutStoreProviderProps) {
  const storeRef = useRef<LayoutStoreInstance | null>(null)

  if (storeRef.current === null) {
    storeRef.current = createLayoutStore({
      issueCommentsCount: 0,
      markers: {},
      pendingReview: {comments: []},
      layoutPullRequest: {
        basePath,
        id: pullRequestId,
        number: pullRequestNumber,
        state: pullRequestState,
      },
    })
  }

  // We disable ESLint refs here as we need our Zustand stores to be a stable reference
  // Using a useState to store our Zustand store will cause unnecessary re-renders and downstream performance issues
  // eslint-disable-next-line react-hooks/refs
  return <LayoutStoreContext value={storeRef.current}>{children}</LayoutStoreContext>
}

const createLayoutStore = (initState: LayoutStoreInitialState) => {
  return createStore<LayoutStoreState>()(
    devtools(
      (...a) => ({
        ...createIssueCommentSlice(initState)(...a),
        ...createMarkersSlice(initState)(...a),
        ...createPendingReviewSlice(initState)(...a),
        layoutPullRequest: initState.layoutPullRequest,
        layoutStoreActions: {
          hydrateMarkers: (markers: MarkersInfo) => {
            const [set] = a
            set({markers}, undefined, 'hydrateMarkers')
          },
        },
      }),
      {name: 'layout-store'},
    ),
  )
}
